import { Extension } from '@tiptap/core'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'
import type { Node as ProseMirrorNode } from '@tiptap/pm/model'
import type { Transaction } from '@tiptap/pm/state'

export interface InlineViewHighlightOptions {
  highlightClass: string
  onRangeClick?: (from: number, to: number) => void
  onRangeHover?: (viewId: string | null) => void
  onRangeModified?: (rangeId: number) => void
}

export interface InlineViewHighlightStorage {
  ranges: Array<{ from: number; to: number; id?: string }>
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    inlineViewHighlight: {
      setInlineViewRanges: (ranges: Array<{ from: number; to: number; id?: string }>) => ReturnType
      clearInlineViewHighlights: () => ReturnType
    }
  }
}

const pluginKey = new PluginKey('inlineViewHighlight')

function createDecorations(
  doc: ProseMirrorNode,
  ranges: Array<{ from: number; to: number; id?: string }>,
  options: InlineViewHighlightOptions
): DecorationSet {
  const decorations: Decoration[] = []
  
  ranges.forEach(range => {
    // Ensure the range is valid for the current document
    if (range.from >= 0 && range.to <= doc.content.size && range.from < range.to) {
      const decoration = Decoration.inline(range.from, range.to, {
        class: options.highlightClass,
        'data-inline-view-id': range.id || '',
        style: 'cursor: pointer;',
      })
      decorations.push(decoration)
    }
  })
  
  return DecorationSet.create(doc, decorations)
}

export const InlineViewHighlight = Extension.create<InlineViewHighlightOptions, InlineViewHighlightStorage>({
  name: 'inlineViewHighlight',

  addOptions() {
    return {
      highlightClass: 'inline-view-highlight',
    }
  },

  addStorage() {
    return {
      ranges: [] as Array<{ from: number; to: number; id?: string }>,
    }
  },

  addProseMirrorPlugins() {
    const extension = this

    return [
      new Plugin({
        key: pluginKey,
        state: {
          init: () => DecorationSet.empty,
          apply(tr: Transaction, oldState: DecorationSet) {
            const meta = tr.getMeta(pluginKey)

            if (meta?.ranges !== undefined) {
              // Store ranges in extension storage
              extension.storage.ranges = meta.ranges

              // Create new decorations
              return createDecorations(
                tr.doc,
                meta.ranges,
                extension.options
              )
            }

            // Document changed, check if any highlighted ranges were modified
            if (tr.docChanged && extension.storage.ranges.length > 0) {
              const modifiedRanges: string[] = []
              
              // Map ranges through the transaction and detect modifications
              const mappedRanges = extension.storage.ranges.map(range => {
                const mappedFrom = tr.mapping.map(range.from)
                const mappedTo = tr.mapping.map(range.to)
                
                // Check if the range content was modified
                if (mappedFrom !== range.from || mappedTo !== range.to) {
                  // Check if content within range was actually changed
                  const oldSize = range.to - range.from
                  const newSize = mappedTo - mappedFrom
                  
                  if (oldSize !== newSize || tr.steps.length > 0) {
                    // Range was modified
                    if (range.id) {
                      modifiedRanges.push(range.id)
                    }
                  }
                }
                
                return {
                  ...range,
                  from: mappedFrom,
                  to: mappedTo
                }
              })
              
              // Notify about modified ranges
              modifiedRanges.forEach(id => {
                if (extension.options.onRangeModified) {
                  // Use setTimeout to avoid state update during transaction
                  setTimeout(() => extension.options.onRangeModified?.(Number(id)), 0)
                }
              })
              
              // Filter out invalid ranges (collapsed or inverted)
              const validRanges = mappedRanges.filter(range => 
                range.from < range.to && range.from >= 0 && range.to <= tr.doc.content.size
              )
              
              extension.storage.ranges = validRanges
              
              return createDecorations(
                tr.doc,
                validRanges,
                extension.options
              )
            }
            
            // Map existing decorations through the transaction
            return oldState.map(tr.mapping, tr.doc)
          },
        },
        props: {
          decorations(state) {
            return this.getState(state)
          },
          handleClick(view, pos, event) {
            if ((extension.storage.ranges as Array<{ from: number; to: number; id?: string }>).length === 0) {
              return false
            }
            // Check if click is on a highlighted range
            let clickedRange: { from: number; to: number } | null = null

            // Check stored ranges directly
            const ranges = extension.storage.ranges as Array<{ from: number; to: number; id?: string }>
            ranges.forEach(range => {
              if (pos >= range.from && pos <= range.to) {
                clickedRange = range
              }
            })

            if (clickedRange !== null && extension.options.onRangeClick) {
              // Call the click handler with the range positions
              const { from, to } = clickedRange as { from: number; to: number }
              extension.options.onRangeClick(from, to)
              event.preventDefault()
              event.stopPropagation()
              return true // Prevent default handling
            }

            return false
          },
          handleDOMEvents: {
            mouseover(view, event) {
              if ((extension.storage.ranges as Array<{ from: number; to: number; id?: string }>).length === 0) {
                return false
              }
              const target = event.target as HTMLElement

              // Check if hovering over a decoration
              if (target.classList?.contains(extension.options.highlightClass) ||
                  target.closest(`.${extension.options.highlightClass}`)) {
                const decorationEl = target.classList?.contains(extension.options.highlightClass)
                  ? target
                  : target.closest(`.${extension.options.highlightClass}`) as HTMLElement

                if (decorationEl) {
                  const viewId = decorationEl.getAttribute('data-inline-view-id')
                  if (viewId && extension.options.onRangeHover) {
                    extension.options.onRangeHover(viewId)
                  }
                }
              }
              return false
            },
            mouseout(view, event) {
              if ((extension.storage.ranges as Array<{ from: number; to: number; id?: string }>).length === 0) {
                return false
              }
              const target = event.target as HTMLElement
              const relatedTarget = event.relatedTarget as HTMLElement

              // Check if leaving a decoration
              if (target.classList?.contains(extension.options.highlightClass) ||
                  target.closest(`.${extension.options.highlightClass}`)) {
                // Check if we're not moving to another element within the same decoration
                if (!relatedTarget?.closest(`.${extension.options.highlightClass}`)) {
                  if (extension.options.onRangeHover) {
                    extension.options.onRangeHover(null)
                  }
                }
              }
              return false
            }
          }
        },
      }),
    ]
  },

  addCommands() {
    const extension = this
    return {
      setInlineViewRanges: (ranges: Array<{ from: number; to: number; id?: string }>) => ({ tr, dispatch }) => {
        if (dispatch) {
          // Update both the plugin state and extension storage
          extension.storage.ranges = ranges
          tr.setMeta(pluginKey, { ranges })
        }
        return true
      },
      
      clearInlineViewHighlights: () => ({ tr, dispatch }) => {
        if (dispatch) {
          // Update both the plugin state and extension storage
          extension.storage.ranges = []
          tr.setMeta(pluginKey, { ranges: [] })
        }
        return true
      },
    }
  },
})
